<script setup lang="ts">
  import {
    computed, onMounted, onUnmounted, ref, watch
  } from 'vue'
  import { useUrlSearchParams } from '@vueuse/core'

  import { useSubjectResource } from '@/composables/useSubjectResource'
  import type { StepTask as StepTaskType } from '@/types/Task'

  import FocusHeader from '@/components/layout/FocusHeader.vue'
  import StepTask from '@/components/task/StepTask.vue'
  import StepTaskFooter from '@/components/task/StepTaskFooter.vue'
  import TaskFrontpage from '@/components/task/TaskFrontpage.vue'

  const { isLoading, isAssignmentCollection, taskFrontpage } = useSubjectResource()
  const params = useUrlSearchParams('history')

  const props = defineProps<{
    tasks: StepTaskType[],
    isStepSuperTitle?: boolean,
    isNumberBullets?: boolean,
  }>()

  const currentPage = ref<number>(0)
  const totalPages = computed(() => props.tasks.length)

  const updateSearchParams = () => {
    if (isAssignmentCollection.value === null || isAssignmentCollection.value) return   // Avoid setting step search params for assignment collections
    params.step = currentPage.value.toString()
  }

  const isFocusFooterVisible = computed(() => !!(totalPages.value > 1 || taskFrontpage.value) && !isLoading.value && props.tasks.length > 0)

  const isFirstPage = computed(() => (currentPage?.value ?? 0) === (taskFrontpage.value ? 0 : 1))
  const isLastPage = computed(() => (currentPage?.value ?? 0) === totalPages?.value)

  watch(taskFrontpage, () => {
    if (currentPage.value !== 0) return   // Avoid incrementing step param when already set (on page refresh + direct links to step > 0)
    currentPage.value = currentPage.value + (taskFrontpage.value ? 0 : 1)
  })
  watch(currentPage, () => updateSearchParams())

  onMounted(() => currentPage.value = (currentPage.value > totalPages.value) ? 0 : parseInt(params.step as string) || 0)
  onUnmounted(() => params.step = '')
</script>

<template>
  <FocusHeader />
  <template v-if="taskFrontpage && currentPage === 0">
    <TaskFrontpage :content-id="taskFrontpage.contentId">
      <template #footer>
        <StepTaskFooter
          v-if="isFocusFooterVisible"
          :is-first-page="isFirstPage"
          :is-last-page="isLastPage"
          @previous="currentPage--"
          @next="currentPage++"
        />
      </template>    
    </TaskFrontpage>
  </template>
  <template
    v-for="(task, index) in tasks"
    :key="task.contentId"
  >
    <StepTask
      :task="task"
      :step="index + 1"
      :current-page="currentPage"
      :total-pages="totalPages"
      :is-focus-footer-visible="isFocusFooterVisible"
    >
      <template #footer>
        <StepTaskFooter
          v-if="isFocusFooterVisible"
          :is-first-page="isFirstPage"
          :is-last-page="isLastPage"
          @previous="currentPage--"
          @next="currentPage++"
        />
      </template>
    </StepTask>
  </template>
</template>
